import React, { useState, useEffect } from 'react';
import sidebarData from '../LayoutMenuDataV2';
import { Link, useLocation } from 'react-router-dom';
import { Collapse } from 'reactstrap';
import './sidebar.css';
import { getDomainQueryParamKeys } from '../../Routes/domainConfig';

// Menu links can carry query strings (?booking_type=..., ?name=..., ?saved_filter_id=...),
// while several items share the same route. Match on pathname plus every query param the
// link defines. startDate/endDate are skipped: they are recomputed from today's date, so
// they can drift from the URL without meaning the user left the page. nextNavigation is a
// navigation hint (often literally "undefined") that pages may drop from the URL.
// Domain defaults (`userVersion` on edge) are skipped too: a page that navigated with a plain
// hard-coded path is still the same screen, it just lost the param, and the item should stay lit.
const VOLATILE_QUERY_PARAMS = ['startDate', 'endDate', 'nextNavigation'];

const isIgnoredQueryParam = key => VOLATILE_QUERY_PARAMS.includes(key) || getDomainQueryParamKeys().includes(key);

const isLinkActive = (link, location) => {
  if (!link || link === '#') return false;
  const [path, query = ''] = link.split('?');
  if (path !== location.pathname) return false;

  const linkParams = new URLSearchParams(query);
  const currentParams = new URLSearchParams(location.search);

  for (const [key, value] of linkParams.entries()) {
    if (isIgnoredQueryParam(key)) continue;
    if (currentParams.get(key) !== value) return false;
  }
  return true;
};

const hasActiveDescendant = (item, location) =>
  item.subItems?.some(child => isLinkActive(child.link, location) || hasActiveDescendant(child, location)) || false;

const VerticalSidebar = () => {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});

  const navData = sidebarData()?.props?.children || [];

  // Auto-expand every ancestor of the currently active route
  useEffect(() => {
    const findActiveTrail = items => {
      for (const item of items) {
        if (isLinkActive(item.link, location)) return [];
        if (item.subItems?.length) {
          const trail = findActiveTrail(item.subItems);
          if (trail) return [item.id, ...trail];
        }
      }
      return null;
    };

    const trail = findActiveTrail(navData) || [];
    if (trail.length) {
      setOpenMenus(prev => {
        const next = { ...prev };
        trail.forEach(id => {
          next[id] = true;
        });
        return next;
      });
    }
  }, [location.pathname, location.search, navData.length]);

  const toggleMenu = id => {
    const collectAllChildIds = item => {
      let ids = [];
      if (item.subItems?.length) {
        for (const child of item.subItems) {
          ids.push(child.id);
          ids = ids.concat(collectAllChildIds(child));
        }
      }
      return ids;
    };

    const findItemAndCollapseChildren = (targetId, items) => {
      for (const item of items) {
        if (item.id === targetId) {
          return collectAllChildIds(item);
        }
        if (item.subItems?.length) {
          const result = findItemAndCollapseChildren(targetId, item.subItems);
          if (result.length) return result;
        }
      }
      return [];
    };

    setOpenMenus(prev => {
      const isCurrentlyOpen = prev[id];
      const newState = { ...prev, [id]: !isCurrentlyOpen };

      if (isCurrentlyOpen) {
        // Collapse all children recursively
        const childIds = findItemAndCollapseChildren(id, navData);
        for (const childId of childIds) {
          newState[childId] = false;
        }
      }

      return newState;
    });
  };

  return (
    <>
      {navData?.map((section, idx) =>
        // A top-level entry with no children is a real destination, not a group
        // heading — render it as a normal (icon-bearing) menu item.
        section?.subItems?.length ? (
          <React.Fragment key={section.id || idx}>
            <li className="menu-title v2-menu-title">
              <span>{section?.label}</span>
            </li>
            {section.subItems.map(item => (
              <SidebarMenuItem key={item.id} item={item} toggleMenu={toggleMenu} openMenus={openMenus} location={location} />
            ))}
          </React.Fragment>
        ) : (
          <SidebarMenuItem key={section.id || idx} item={section} toggleMenu={toggleMenu} openMenus={openMenus} location={location} />
        ),
      )}
    </>
  );
};

export default VerticalSidebar;

const SidebarMenuItem = ({ item, toggleMenu, openMenus, location, depth = 0 }) => {
  const hasChildren = item.subItems?.length > 0;
  const hasNestedGroups = item.subItems?.some(child => child.subItems?.length > 0) || false;
  const isOpen = !!openMenus[item.id];
  const isActive = isLinkActive(item.link, location);
  const isParentActive = hasChildren && hasActiveDescendant(item, location);

  const linkClasses = ['nav-link', 'menu-link', 'v2-menu-link', isActive ? 'active' : '', isParentActive ? 'v2-parent-active' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <li className="nav-item">
      <Link
        to={item.link || '#'}
        className={linkClasses}
        aria-expanded={hasChildren ? isOpen : undefined}
        onClick={e => {
          if (hasChildren) {
            e.preventDefault();
            toggleMenu(item.id);
          }
        }}
      >
        {item.icon && <i className={item.icon}></i>}
        <span>{item.label}</span>
        {hasChildren && <i className={`ri-arrow-right-s-line v2-menu-arrow ${isOpen ? 'v2-menu-arrow-open' : ''}`}></i>}
      </Link>

      {/* A collapsed-sidebar flyout may only scroll when none of its children open
          a nested flyout of their own — any overflow other than visible clips those. */}
      {hasChildren && (
        <Collapse isOpen={isOpen} className={`menu-dropdown${hasNestedGroups ? '' : ' v2-flyout-scroll'}`}>
          <ul className="nav nav-sm flex-column v2-submenu">
            {/* Only visible in the collapsed sidebar, where the submenu becomes a flyout
                panel and the parent's own label is no longer readable in the rail. */}
            <li className="v2-flyout-title">{item.label}</li>
            {item.subItems.map((child, idx) => (
              <SidebarMenuItem
                key={child.id || idx}
                item={child}
                toggleMenu={toggleMenu}
                openMenus={openMenus}
                location={location}
                depth={depth + 1}
              />
            ))}
          </ul>
        </Collapse>
      )}
    </li>
  );
};
